package com.elan.edubridge.ui.member

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.AcademyApplication
import com.elan.edubridge.shared.domain.model.AcademyApplicationRequest
import com.elan.edubridge.shared.domain.model.AcademyApplicationStatus
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.JoinApplication
import com.elan.edubridge.shared.domain.model.JoinApplicationStatus
import com.elan.edubridge.shared.domain.model.JoinCodePreviewStatus
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.ReadResult
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.ui.shell.Router
import com.elan.edubridge.ui.shell.StoreLinks
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.managerAppLink
import com.elan.edubridge.ui.shell.formatKstDateTime
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.readErrorHtml
import com.elan.edubridge.ui.shell.wireRetry
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlin.js.Date
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement

/**
 * `/app/me/academy-application` — 학원과 연결되지 않은 사용자의 진입로 두 갈래를 한 화면에 둔다:
 * ① 참여 코드로 기존 학원에 **가입** 신청(W7, 학원 스태프가 승인), ② 새 학원 **개설** 신청
 * (앱 결제로 자동 활성화, 플랫폼 관리자 승인은 예외). 학부모는 둘 다 아니다 — 학원에서 받은 초대
 * 링크로만 등록한다(§2.6).
 */
object AcademyApplicationPage {
    private val scope = MainScope()

    /** 신청 상태 조회 실패의 "다시 시도" 버튼 id(#30). */
    private const val RETRY_ID = "academy-application-retry"
    private val accountRepository = SharedGraph.createAccountRepository()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()

    fun render(session: SessionState) {
        scope.launch {
            val context = loadMemberContext(session)
            MemberShell.render(context, activeTab = null, bodyHtml = loadingHtml())

            val applicantUid = context.session.account?.id
            // 내 신청 상태를 못 읽은 것을 "신청 없음"으로 그리면 사용자가 이미 낸 신청을 다시
            // 내려 하고, 서버는 그것을 중복으로 거부한다(#30).
            val applications = if (applicantUid == null) {
                ReadResult()
            } else {
                context.repository.myAcademyApplicationsResult(applicantUid)
            }
            val joinApplications = if (applicantUid == null) {
                ReadResult()
            } else {
                context.repository.myJoinApplicationsResult(applicantUid)
            }
            renderLoaded(context, applications, joinApplications)
        }
    }

    private suspend fun renderLoaded(
        context: MemberContext,
        applicationsResult: ReadResult<AcademyApplication>,
        joinApplicationsResult: ReadResult<JoinApplication>,
    ) {
        val applications = applicationsResult.items
        MemberShell.render(
            context,
            activeTab = null,
            bodyHtml = bodyHtml(context, applicationsResult, joinApplicationsResult),
        )
        wireRetry(RETRY_ID) { render(context.session) }
        wireJoinSection(context.session)
        applications.firstOrNull { it.status == AcademyApplicationStatus.APPROVED }
            ?.academyId
            ?.takeIf { it.isNotBlank() }
            ?.let(::wireApprovedConsole)
        if (canSubmit(context.session) && applications.none { it.status == AcademyApplicationStatus.PENDING || it.status == AcademyApplicationStatus.APPROVED }) {
            wireForm(context.session)
        }
    }

    private fun bodyHtml(
        context: MemberContext,
        applicationsResult: ReadResult<AcademyApplication>,
        joinApplicationsResult: ReadResult<JoinApplication>,
    ): String {
        val applications = applicationsResult.items
        val joinApplications = joinApplicationsResult.items
        // 두 조회 중 하나라도 실패하면 아래 폼이 "아직 신청 안 함"을 전제하게 되므로 먼저 알린다.
        val readFailure = applicationsResult.failure ?: joinApplicationsResult.failure
        if (readFailure != null && applications.isEmpty() && joinApplications.isEmpty()) {
            return """
                <div class="eb-page-header">
                    <div><h1 class="eb-page-title">학원 연결</h1></div>
                </div>
                ${readErrorHtml(readFailure, RETRY_ID)}
            """.trimIndent()
        }
        val pending = applications.firstOrNull { it.status == AcademyApplicationStatus.PENDING }
        val approved = applications.firstOrNull { it.status == AcademyApplicationStatus.APPROVED }
        val activeAcademyId = context.session.activeMembership?.academyId
        val content = when {
            pending != null -> pendingHtml(pending)
            approved != null -> approvedHtml(approved)
            activeAcademyId != null -> activeMembershipHtml(activeAcademyId)
            !canSubmit(context.session) -> unavailableHtml()
            else -> formHtml()
        }
        return """
            <div class="eb-page-header">
                <div><h1 class="eb-page-title">학원 연결</h1><p class="eb-meta">참여 코드로 학원에 가입하거나, 새 학원 개설을 신청할 수 있어요.</p></div>
            </div>
            ${joinSectionHtml(joinApplications)}
            <div class="eb-page-header">
                <div><h2>학원 개설 신청</h2><p class="eb-meta">플랜 선택과 결제는 ${managerAppLink()} 앱(Google Play)에서 진행해요.</p></div>
            </div>
            $content
        """.trimIndent()
    }

    // ---- W7 참여 코드 가입 ----

    private fun joinSectionHtml(joinApplications: List<JoinApplication>): String = """
        <section class="eb-card" aria-label="참여 코드로 학원 가입">
            <h2>참여 코드로 학원 가입</h2>
            <p>학원에서 안내받은 8자리 참여 코드를 입력해 주세요. 학원이 승인하면 학생으로 연결돼요.</p>
            ${joinApplications.joinToString("") { joinStatusHtml(it) }}
            <form id="join-code-form" novalidate>
                <div class="eb-field">
                    <label for="join-code-input">참여 코드</label>
                    <input id="join-code-input" maxlength="16" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="예: AB12-CD34">
                </div>
                <p class="eb-form-error" id="join-code-error" role="alert" hidden></p>
                <button class="eb-button eb-button--primary" id="join-code-preview" type="submit">코드 확인</button>
            </form>
            <div id="join-preview-panel" hidden></div>
            <p class="eb-meta">학부모는 참여 코드가 아니라 학원에서 받은 초대 링크로 등록해 주세요.</p>
        </section>
    """.trimIndent()

    /** 내 신청 한 건의 상태 행. 취소·쿨다운·차단이 전부 이 행에서 설명된다. */
    private fun joinStatusHtml(application: JoinApplication): String {
        val name = application.academyName.ifBlank { "학원" }.escapeHtml()
        val (chipClass, caption, action) = when (application.status) {
            JoinApplicationStatus.PENDING -> Triple(
                "eb-chip--warning",
                "학원이 신청을 검토하고 있어요.",
                """<button class="eb-button eb-button--ghost eb-button--sm join-application-cancel" type="button" data-academy-id="${application.academyId.escapeHtml()}">신청 취소</button>""",
            )
            JoinApplicationStatus.APPROVED -> Triple(
                "eb-chip--success",
                "가입이 승인됐어요. 화면을 새로고침하면 학원 메뉴가 나타나요.",
                "",
            )
            JoinApplicationStatus.REJECTED, JoinApplicationStatus.CANCELLED -> Triple(
                "eb-chip--info",
                cooldownCaption(application),
                "",
            )
            JoinApplicationStatus.BLOCKED -> Triple(
                "eb-chip--error",
                // 서버 문구와 동일한 일반 문구 — 차단 사유는 노출하지 않는다.
                "지금은 이 학원에 가입 신청을 할 수 없어요. 학원에 직접 문의해 주세요.",
                "",
            )
        }
        return """
            <article class="eb-notice-item">
                ${icon("icon-academy", "md")}
                <div>
                    <strong>$name</strong> <span class="eb-chip $chipClass">${application.status.displayName}</span>
                    <span class="eb-meta">$caption</span>
                </div>
                <div class="eb-notice-actions">$action</div>
            </article>
        """.trimIndent()
    }

    /** 반려·취소 후 24시간 쿨다운(서버 `JOIN_REAPPLY_COOLDOWN_HOURS`)의 사용자 안내. */
    private fun cooldownCaption(application: JoinApplication): String {
        val decidedAt = application.decidedAt ?: return "다시 신청할 수 있어요."
        val readyAt = Date(Date(decidedAt).getTime() + 24 * 60 * 60 * 1000)
        if (readyAt.getTime() <= Date().getTime()) return "다시 신청할 수 있어요."
        return "${formatKstDateTime(readyAt.toISOString())} 이후에 다시 신청할 수 있어요."
    }

    private fun wireJoinSection(session: SessionState) {
        (document.getElementById("join-code-form") as? HTMLFormElement)?.onsubmit = { event ->
            event.preventDefault()
            previewJoinCode(session)
            null
        }
        val cancelButtons = document.querySelectorAll(".join-application-cancel")
        for (index in 0 until cancelButtons.length) {
            val button = cancelButtons.item(index) as? HTMLButtonElement ?: continue
            button.onclick = {
                val academyId = button.getAttribute("data-academy-id")
                if (academyId != null) {
                    button.disabled = true
                    scope.launch {
                        val result = commandRepository.cancelAcademyJoinApplication(academyId)
                        if (!result.isSuccess) {
                            button.disabled = false
                            showJoinError(result.errorMessage ?: "신청을 취소하지 못했어요. 다시 시도해 주세요.")
                        } else {
                            render(session)
                        }
                    }
                }
                null
            }
        }
    }

    private fun previewJoinCode(session: SessionState) {
        val input = document.getElementById("join-code-input") as? HTMLInputElement ?: return
        // 정규화는 서버 몫이지만, 빈 입력까지 예산(시간당 12회, 제출과 공유)을 쓰게 두지는 않는다.
        val raw = input.value.trim()
        if (raw.isEmpty()) {
            showJoinError("참여 코드를 입력해 주세요.")
            return
        }
        hideJoinError()
        val previewButton = document.getElementById("join-code-preview") as? HTMLButtonElement
        previewButton?.disabled = true
        scope.launch {
            val result = commandRepository.previewAcademyJoinCode(raw)
            previewButton?.disabled = false
            val academyName = result.academyName
            if (!result.isSuccess) {
                hideJoinPreview()
                showJoinError(result.errorMessage ?: "참여 코드를 확인하지 못했어요. 다시 시도해 주세요.")
            } else if (result.status != JoinCodePreviewStatus.USABLE || academyName == null) {
                hideJoinPreview()
                showJoinError("사용할 수 없는 참여 코드예요. 학원에 다시 확인해 주세요.")
            } else {
                showJoinPreview(session, raw, academyName)
            }
        }
    }

    /** 프리뷰 성공 후에만 동의 체크박스와 제출 버튼이 나타난다 — 동의 대상 학원명이 그때 확정된다. */
    private fun showJoinPreview(session: SessionState, code: String, academyName: String) {
        val panel = document.getElementById("join-preview-panel") as? HTMLElement ?: return
        val name = academyName.escapeHtml()
        panel.innerHTML = """
            <div class="eb-field">
                <p><strong>$name</strong>에 가입을 신청할까요?</p>
                <label><input type="checkbox" id="join-consent"> 내 이름과 이메일이 ${name}에 전달되는 것에 동의해요</label>
            </div>
            <p class="eb-form-error" id="join-submit-error" role="alert" hidden></p>
            <button class="eb-button eb-button--primary" id="join-submit" type="button" disabled>가입 신청</button>
        """.trimIndent()
        panel.removeAttribute("hidden")

        val consent = panel.querySelector("#join-consent") as? HTMLInputElement
        val submit = panel.querySelector("#join-submit") as? HTMLButtonElement
        consent?.onchange = {
            submit?.disabled = consent.checked != true
            null
        }
        submit?.onclick = {
            if (consent?.checked == true) {
                submit.disabled = true
                scope.launch {
                    val result = commandRepository.submitAcademyJoinApplication(code, consentAgreed = true)
                    if (result.isSuccess) {
                        render(session)
                    } else {
                        submit.disabled = false
                        (panel.querySelector("#join-submit-error") as? HTMLElement)?.let { error ->
                            error.textContent = result.errorMessage ?: "가입 신청에 실패했어요. 다시 시도해 주세요."
                            error.removeAttribute("hidden")
                        }
                    }
                }
            }
            null
        }
    }

    private fun hideJoinPreview() {
        (document.getElementById("join-preview-panel") as? HTMLElement)?.let {
            it.innerHTML = ""
            it.setAttribute("hidden", "")
        }
    }

    private fun showJoinError(message: String) {
        (document.getElementById("join-code-error") as? HTMLElement)?.let { error ->
            error.textContent = message
            error.removeAttribute("hidden")
        }
    }

    private fun hideJoinError() {
        (document.getElementById("join-code-error") as? HTMLElement)?.setAttribute("hidden", "")
    }

    // ---- 학원 개설 신청 ----

    private fun loadingHtml(): String = """
        <div class="eb-page-header"><div><h1 class="eb-page-title">학원 연결</h1><p class="eb-meta">신청 상태를 불러오는 중이에요.</p></div></div>
        <section class="eb-card" aria-live="polite">${icon("icon-refresh", "md", "eb-spin")}신청 상태를 불러오는 중이에요.</section>
    """.trimIndent()

    private fun pendingHtml(application: AcademyApplication): String = """
        <section class="eb-card" aria-label="학원 개설 신청 상태">
            <div class="eb-page-header"><h2>${application.academyName.escapeHtml()}</h2><span class="eb-chip eb-chip--warning">승인 대기</span></div>
            <p>신청이 접수됐어요. 플랜 선택과 결제는 ${managerAppLink()} 앱(Google Play)에서 이어서 진행하면 학원이 바로 활성화돼요.</p>
            ${if (StoreLinks.managerPlay == null) """<p class="eb-meta">관리자 앱은 지금 Google Play 심사 중이에요. 게시되면 이 화면에 스토어 링크가 표시돼요.</p>""" else ""}
        </section>
    """.trimIndent()

    private fun approvedHtml(application: AcademyApplication): String {
        val academyId = application.academyId?.takeIf { it.isNotBlank() }
        val consoleLink = academyId?.let {
            """<button class="eb-button eb-button--primary" id="approved-academy-console" data-academy-id="${it.escapeHtml()}" type="button">학원 콘솔로 이동</button>"""
        }.orEmpty()
        return """
            <section class="eb-card" aria-label="학원 개설 신청 상태">
                <div class="eb-page-header"><h2>${application.academyName.escapeHtml()}</h2><span class="eb-chip eb-chip--success">승인됨</span></div>
                <p>학원 개설 신청이 승인되었어요.</p>
                <p class="eb-form-error" id="approved-console-error" role="alert" hidden></p>
                $consoleLink
            </section>
        """.trimIndent()
    }

    private fun activeMembershipHtml(academyId: String): String = """
        <section class="eb-card">
            <p>이미 소속된 학원이 있어요.</p>
            <a class="eb-button eb-button--primary" href="/app/${academyId.escapeHtml()}/today">학원 콘솔로 이동</a>
        </section>
    """.trimIndent()

    private fun unavailableHtml(): String = """
        <section class="eb-card">
            <p>학원 개설 신청은 학원에 소속되지 않은 뷰어 계정에서만 할 수 있어요.</p>
        </section>
    """.trimIndent()

    private fun formHtml(): String = """
        <section class="eb-card">
            <form id="academy-application-form" novalidate>
                <div class="eb-field">
                    <label for="academy-name">학원명</label>
                    <input id="academy-name" maxlength="60" required aria-describedby="academy-name-help">
                    <p id="academy-name-help" class="eb-meta">2~60자로 입력해 주세요.</p>
                </div>
                <div class="eb-field">
                    <label for="owner-name">대표자명</label>
                    <input id="owner-name" maxlength="50">
                </div>
                <div class="eb-field">
                    <label for="contact-phone">연락처</label>
                    <input id="contact-phone" type="tel" maxlength="20" placeholder="010-0000-0000">
                    <p class="eb-meta">선택 입력 · 숫자, +, -, 괄호, 공백을 포함해 7~20자로 입력할 수 있어요.</p>
                </div>
                <div class="eb-field">
                    <label for="academy-address">주소</label>
                    <input id="academy-address" maxlength="200">
                </div>
                <div class="eb-field">
                    <label for="academy-description">학원 소개</label>
                    <textarea id="academy-description" class="eb-textarea" maxlength="500"></textarea>
                </div>
                <p class="eb-form-error" id="academy-application-error" role="alert" hidden></p>
                <button class="eb-button eb-button--primary" id="academy-application-submit" type="submit">신청하기</button>
            </form>
        </section>
    """.trimIndent()

    private fun wireForm(session: SessionState) {
        val form = document.getElementById("academy-application-form") as? HTMLFormElement ?: return
        form.onsubmit = { event ->
            event.preventDefault()
            submit(session)
            null
        }
    }

    private fun wireApprovedConsole(academyId: String) {
        val button = document.getElementById("approved-academy-console") as? HTMLButtonElement ?: return
        button.onclick = {
            button.disabled = true
            scope.launch {
                try {
                    val refreshed = accountRepository.forceRefreshToken()
                    if (refreshed.usableMemberships.any { it.academyId == academyId }) {
                        accountRepository.selectAcademy(academyId)
                        Router.navigate("/app/$academyId/today")
                    } else {
                        button.disabled = false
                        showApprovedConsoleError("승인된 학원 정보를 확인하지 못했어요. 잠시 후 다시 시도해 주세요.")
                    }
                } catch (_: Throwable) {
                    button.disabled = false
                    showApprovedConsoleError("학원 정보를 새로고침하지 못했어요. 잠시 후 다시 시도해 주세요.")
                }
            }
            null
        }
    }

    private fun submit(session: SessionState) {
        val request = AcademyApplicationRequest(
            academyName = inputValue("academy-name").trim(),
            ownerName = inputValue("owner-name").trim(),
            phone = inputValue("contact-phone").trim(),
            address = inputValue("academy-address").trim(),
            description = textareaValue("academy-description").trim(),
        )
        val validationError = validationError(request)
        if (validationError != null) {
            showError(validationError)
            return
        }

        val submitButton = document.getElementById("academy-application-submit") as? HTMLButtonElement
        submitButton?.disabled = true
        scope.launch {
            val result = commandRepository.submitAcademyApplication(request)
            if (result.isSuccess) {
                render(session)
            } else if (result.failure == CommandFailure.ALREADY_EXISTS) {
                render(session)
            } else {
                submitButton?.disabled = false
                showError(result.errorMessage ?: "학원 개설 신청에 실패했어요. 다시 시도해 주세요.")
            }
        }
    }

    private fun validationError(request: AcademyApplicationRequest): String? = when {
        request.academyName.length !in 2..60 -> "학원명은 2~60자로 입력해 주세요."
        request.ownerName.length > 50 -> "대표자명은 50자 이하로 입력해 주세요."
        request.phone.isNotEmpty() && !PHONE_PATTERN.matches(request.phone) -> "연락처는 숫자, +, -, 괄호, 공백을 포함해 7~20자로 입력해 주세요."
        request.address.length > 200 -> "주소는 200자 이하로 입력해 주세요."
        request.description.length > 500 -> "학원 소개는 500자 이하로 입력해 주세요."
        else -> null
    }

    private fun inputValue(id: String): String = (document.getElementById(id) as? HTMLInputElement)?.value.orEmpty()

    private fun textareaValue(id: String): String = (document.getElementById(id) as? HTMLTextAreaElement)?.value.orEmpty()

    private fun showError(message: String) {
        val error = document.getElementById("academy-application-error") ?: return
        error.textContent = message
        error.removeAttribute("hidden")
    }

    private fun showApprovedConsoleError(message: String) {
        val error = document.getElementById("approved-console-error") ?: return
        error.textContent = message
        error.removeAttribute("hidden")
    }

    private fun canSubmit(session: SessionState): Boolean =
        session.activeMembership == null && session.activePermission == Permission.VIEWER

    private val PHONE_PATTERN = Regex("^[0-9+\\-() ]{7,20}$")
}
